import { useQuery } from '@tanstack/react-query'
import { Download, TrendingDown, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { statistics as statisticsApi } from '@/api/endpoints'
import DateInput from '@/components/ui/DateInput'
import { PlanLock, usePlanFeature } from '@/features/billing/PlanFeature'
import { EmptyState, Field, Spinner } from '@/components/ui'
import {
  addDays,
  cn,
  formatMoney,
  formatNumber,
  minutesToHours,
  quickRange,
  shiftRange,
  toISODate,
  type DateRange,
} from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { StatisticsResponse } from '@/types/api'
import { CenterFilter, useCenterFilter } from '@/features/centers/useCenters'

/** Paleta pôvodnej aplikácie - www/css/config.less + style.less. */
const PALETTE = ['#a4091e', '#287fdd', '#bcd631', '#86417b', '#ce9409', '#698cfe', '#12a00a']
const BAR_PRIMARY = '#287fdd'
const BAR_SECONDARY = '#86417b'
const SHADOW = '#c9d6e4'
const WEEKDAYS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']

export default function StatisticsPage() {
  const { t } = useTranslation()
  const isAdmin = Boolean(useAuth((s) => s.user)?.can_administrate)
  // Základné štatistiky má každý balíček; vlastné obdobie, porovnanie,
  // rozpady a export sú od Profi (server ich inak ani nepošle).
  const pro = usePlanFeature('statistics_pro').allowed
  const canExport = usePlanFeature('accounting_export').allowed
  const [interval, setInterval] = useState('this-season')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [downloading, setDownloading] = useState(false)

  // Vlastné obdobie sa pýta až s oboma dátumami - s jedným by server
  // potichu vrátil sezónu a vyzeralo by to, že filter nefunguje.
  const datesMissing = interval === 'custom' && !(from && to)
  // Strediská (`sql/040`) - spoločný filter s kalendárom a platbami.
  const [centerIds] = useCenterFilter()
  const { data, isLoading } = useQuery({
    queryKey: ['statistics', interval, from, to, centerIds.join(',')],
    queryFn: () => statisticsApi.get(interval, from || undefined, to || undefined, centerIds),
    enabled: isAdmin && !datesMissing,
    // Zoznam sezón vo výbere ostáva, kým sa dopĺňajú dátumy.
    placeholderData: (previous) => previous,
  })

  /**
   * Ktorá položka výberu je zvolená.
   *
   * `this-season` a `last-season` server rozumie naďalej — sú v uložených
   * odkazoch a v záložkách — ale vo výbere sa ukazujú ako konkrétna sezóna.
   * Bez tohto prekladu by sa pri prvom načítaní vysvietila prvá položka,
   * hoci stav hovorí niečo iné.
   */
  const seasons = data?.seasons ?? []
  const selected =
    interval === 'this-season'
      ? seasons.find((season) => season.current)?.value ?? interval
      : interval === 'last-season'
        ? seasons[seasons.findIndex((season) => season.current) + 1]?.value ?? interval
        : interval

  /** Prepnutie obdobia. Vlastné obdobie začína s prázdnymi dátumami. */
  function pickInterval(value: string) {
    if (value === 'custom') {
      setFrom('')
      setTo('')
    }
    setInterval(value)
  }

  /** Rýchly filter = vlastné obdobie s vyplnenými dátumami. */
  function pickRange(range: DateRange) {
    setFrom(range.from)
    setTo(range.to)
    setInterval('custom')
  }

  async function download() {
    setDownloading(true)
    try {
      const { blob, filename } = await statisticsApi.exportUrl(
        interval,
        from || undefined,
        to || undefined,
        centerIds,
      )
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      // Bez uvoľnenia by si prehliadač držal celý súbor v pamäti až do
      // zatvorenia karty.
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  // Len vlastník a administrátor (VOP 1.1); server ostatných odmietne.
  if (!isAdmin) return <Navigate to="/calendar" replace />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="mb-1 text-lg font-semibold">{t('statistics.title')}</h1>
        <CenterFilter className="w-56" />

        {/* Sezóny chodia zo servera — vie, ktoré škola naozaj má. Pevný
            zoznam by prvej sezóne ponúkal roky bez dát a prevzatej škole
            s desiatimi rokmi histórie by polovicu zamlčal. */}
        <select
          value={selected}
          onChange={(e) => pickInterval(e.target.value)}
          className="input w-auto"
        >
          {(data?.seasons ?? []).map((season) => (
            <option key={season.value} value={season.value}>
              {season.label}
              {season.current ? ` — ${t('statistics.thisSeason')}` : ''}
            </option>
          ))}
          {pro ? <option value="custom">{t('statistics.custom')}</option> : null}
        </select>

        {pro ? (
          <div className="mb-1 flex flex-wrap items-center gap-1">
            {quickRanges().map(([key, range]) => {
              const active = interval === 'custom' && from === range.from && to === range.to
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickRange(range)}
                  className={cn(
                    'rounded border px-2 py-1 text-[12px]',
                    active
                      ? 'border-brand-500 bg-brand-50 font-medium text-brand-700'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {t(`statistics.quick.${key}`)}
                </button>
              )
            })}
            <button
              type="button"
              onClick={() => pickInterval('custom')}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-[12px]
                         text-slate-600 hover:bg-slate-50"
            >
              {t('statistics.custom')}
            </button>
          </div>
        ) : null}

        {interval === 'custom' ? (
          <>
            <Field label={t('global.from')} className="w-40">
              <DateInput value={from} onChange={setFrom} />
            </Field>
            <Field label={t('global.to')} className="w-40">
              <DateInput value={to} onChange={setTo} />
            </Field>
          </>
        ) : null}

        {isAdmin && canExport ? (
          <button
            type="button"
            className="btn-secondary ml-auto"
            onClick={download}
            disabled={downloading || !data}
          >
            {downloading ? <Spinner /> : <Download size={15} />}
            {t('statistics.export')}
          </button>
        ) : null}
      </div>

      {datesMissing ? (
        <EmptyState>{t('statistics.pickDates')}</EmptyState>
      ) : isLoading || !data ? (
        <div className="flex h-40 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <Report data={data} />
      )}

      {data && data.pro === false ? (
        <div className="card space-y-1 p-4">
          <h2 className="text-sm font-semibold">{t('statistics.proTitle')}</h2>
          <p className="text-xs text-slate-500">{t('statistics.proText')}</p>
          <PlanLock feature="statistics_pro" />
        </div>
      ) : null}
    </div>
  )
}

function Report({ data }: { data: StatisticsResponse }) {
  const { t } = useTranslation()
  const previous = data.previous
  const money = data.money

  const timeline = data.timeline.map((row) => {
    const shadow = previous?.timeline.find((old) => old.label === row.label)
    return {
      label: row.label,
      lessons: row.lessons,
      revenue: Number(row.revenue),
      // Tieň sa páruje podľa popisku obdobia. Keď mu nič nezodpovedá,
      // zostane `undefined` - nula by v grafe vyzerala ako prepad.
      previousLessons: shadow ? shadow.lessons : undefined,
    }
  })

  const durationData = Object.entries(data.duration_of_lessons ?? {})
    .map(([minutes, count]) => ({ label: `${minutes} min`, count, minutes: Number(minutes) }))
    .sort((a, b) => a.minutes - b.minutes)

  const weekdayData = Object.entries(data.reservations_per_day_of_week ?? {}).map(
    ([day, count]) => ({ label: WEEKDAYS[Number(day)] ?? day, count }),
  )

  return (
    <>
      {/* Prvý riadok odpovedá na „ako sa nám darí“, druhý na „koľko z toho
          máme“. Porovnanie s vlaňajškom je pri tých číslach, kde má zmysel -
          pri dostupných hodinách by porovnávalo veľkosť tímu, nie výkon. */}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat
          label={t('statistics.lessons')}
          value={String(data.overview.lessons)}
          previous={previous?.overview.lessons}
          current={data.overview.lessons}
        />
        <Stat
          label={t('statistics.totalHours')}
          value={minutesToHours(data.overview.total_minutes)}
          previous={previous?.overview.total_hours}
          current={data.overview.total_hours}
        />
        <Stat
          label={t('statistics.utilisation')}
          value={
            data.overview.utilisation === null ? '—' : `${formatNumber(data.overview.utilisation)} %`
          }
          hint={t('statistics.utilisationHint', {
            hours: minutesToHours(data.overview.available_minutes),
          })}
          previous={previous?.overview.utilisation ?? undefined}
          current={data.overview.utilisation ?? undefined}
        />
        <Stat
          label={t('statistics.clients')}
          value={String(data.overview.clients)}
          hint={t('statistics.newClients', { count: data.overview.new_clients })}
          previous={previous?.overview.clients}
          current={data.overview.clients}
        />
        <Stat
          label={t('statistics.cancelled')}
          value={`${formatNumber(data.overview.cancelled_share)} %`}
          hint={t('statistics.cancelledCount', { count: data.overview.cancelled })}
          previous={previous?.overview.cancelled_share}
          current={data.overview.cancelled_share}
          // Pri stornách je rast tá horšia správa.
          inverted
        />
        <Stat
          label={t('statistics.instructors')}
          value={String(data.overview.instructors)}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={t('statistics.revenue')} value={formatMoney(money.revenue)} />
        <Stat label={t('statistics.paid')} value={formatMoney(money.paid)} />
        <Stat
          label={t('statistics.unpaid')}
          value={formatMoney(money.unpaid)}
          hint={t('statistics.unpaidLessons', { count: money.unpaid_lessons })}
          highlight={Number(money.unpaid) > 0}
        />
        <Stat
          label={t('statistics.perLesson')}
          value={formatMoney(money.per_lesson)}
          hint={t('statistics.perClient', { value: formatMoney(money.per_client) })}
        />
      </div>

      <Card title={t('statistics.timeline')} subtitle={t(`statistics.step.${data.step}`)}>
        {timeline.length === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => formatNumber(value as number)} />
              <Legend />
              {previous ? (
                <Bar
                  yAxisId="left"
                  dataKey="previousLessons"
                  name={t('statistics.lastYear')}
                  fill={SHADOW}
                />
              ) : null}
              <Bar
                yAxisId="left"
                dataKey="lessons"
                name={t('statistics.lessons')}
                fill={BAR_PRIMARY}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                name={t('statistics.revenue')}
                stroke={PALETTE[0]}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Rozšírené štatistiky - server ich v základných nepošle. */}
      {data.pro !== false ? (
        <>
          <Card title={t('statistics.instructorLoad')}>
            {(data.instructors ?? []).length === 0 ? (
              <EmptyState>{t('app.empty')}</EmptyState>
            ) : (
              <table className="table-base">
                <thead>
                  <tr>
                    <th>{t('reservation.instructor')}</th>
                    <th className="text-right">{t('statistics.lessons')}</th>
                    <th className="text-right">{t('statistics.taughtHours')}</th>
                    <th className="text-right">{t('statistics.availableHours')}</th>
                    <th>{t('statistics.utilisation')}</th>
                    <th className="text-right">{t('statistics.revenue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.instructors ?? []).map((row) => (
                    <tr key={row.instructor_id}>
                      <td>{row.name || '—'}</td>
                      <td className="text-right tabular-nums">{row.lessons}</td>
                      <td className="text-right tabular-nums">{formatNumber(row.hours)}</td>
                      <td className="text-right tabular-nums text-slate-500">
                        {row.available_hours ? formatNumber(row.available_hours) : '—'}
                      </td>
                      <td className="w-48">
                        <UtilisationBar value={row.utilisation} />
                      </td>
                      <td className="text-right tabular-nums">{formatMoney(row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card title={t('statistics.byCourse')}>
              <Breakdown rows={data.courses ?? []} />
            </Card>
            <Card title={t('statistics.byLanguage')}>
              <Breakdown rows={data.languages ?? []} />
            </Card>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card title={t('statistics.byWeekday')}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={weekdayData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip formatter={(value) => formatNumber(value as number)} />
                  <Bar dataKey="count" name={t('statistics.lessons')} fill={BAR_SECONDARY} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card title={t('statistics.byDuration')}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={durationData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip formatter={(value) => formatNumber(value as number)} />
                  <Bar dataKey="count" name={t('statistics.lessons')}>
                    {durationData.map((row, index) => (
                      <Cell key={row.label} fill={PALETTE[index % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>
      ) : null}
    </>
  )
}

/** Vyťaženie ako pásik: číslo samo o sebe sa v tabuľke stráca. */
function UtilisationBar({ value }: { value: number | null }) {
  if (value === null) return <span className="text-xs text-slate-400">—</span>

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 overflow-hidden rounded bg-slate-100">
        <div
          className={cn(
            'h-full rounded',
            value > 100 ? 'bg-amber-500' : value >= 60 ? 'bg-green-500' : 'bg-blue-400',
          )}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
      <span className="w-12 text-right text-xs tabular-nums text-slate-600">
        {formatNumber(value)} %
      </span>
    </div>
  )
}

function Breakdown({ rows }: { rows: Array<{ label: string; lessons: number; share: number }> }) {
  const { t } = useTranslation()
  if (rows.length === 0) return <EmptyState>{t('app.empty')}</EmptyState>

  return (
    <table className="table-base">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td>{row.label}</td>
            <td className="w-1/2">
              <div className="h-2 overflow-hidden rounded bg-slate-100">
                <div
                  className="h-full rounded bg-brand-500"
                  style={{ width: `${row.share}%` }}
                />
              </div>
            </td>
            <td className="w-24 text-right tabular-nums text-slate-600">
              {row.lessons} · {formatNumber(row.share)} %
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Card({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-baseline gap-2 border-b border-slate-200 px-3 py-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {subtitle ? <span className="text-xs text-slate-400">{subtitle}</span> : null}
      </div>
      {/* Široká tabuľka (inštruktori) sa na mobile posúva do strany. */}
      <div className="overflow-x-auto p-3">{children}</div>
    </div>
  )
}

function Stat({
  label,
  value,
  hint,
  previous,
  current,
  highlight,
  inverted,
}: {
  label: string
  value: string
  hint?: string
  previous?: number
  current?: number
  highlight?: boolean
  inverted?: boolean
}) {
  return (
    <div className="card p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className={cn(
          'mt-0.5 text-xl font-semibold',
          highlight ? 'text-red-600' : 'text-ink',
        )}
      >
        {value}
      </div>
      {hint ? <div className="text-xs text-slate-400">{hint}</div> : null}
      <Change previous={previous} current={current} inverted={inverted} />
    </div>
  )
}

/**
 * Zmena oproti rovnakému obdobiu vlani.
 *
 * Pri nule vlani sa percento nepočíta - delenie nulou dáva „+∞ %“, čo nie je
 * informácia, ale ozdoba.
 */
function Change({
  previous,
  current,
  inverted,
}: {
  previous?: number
  current?: number
  inverted?: boolean
}) {
  const { t } = useTranslation()
  if (previous === undefined || current === undefined || !previous) return null

  const change = Math.round(((current - previous) / previous) * 1000) / 10
  if (change === 0) {
    return <div className="mt-1 text-xs text-slate-400">{t('statistics.sameAsLastYear')}</div>
  }

  const better = inverted ? change < 0 : change > 0
  const Icon = change > 0 ? TrendingUp : TrendingDown

  return (
    <div
      className={cn(
        'mt-1 flex items-center gap-1 text-xs',
        better ? 'text-green-600' : 'text-red-600',
      )}
      title={t('statistics.lastYearValue', { value: formatNumber(previous) })}
    >
      <Icon size={12} />
      {change > 0 ? '+' : ''}
      {formatNumber(change)} %
    </div>
  )
}

/** Rýchle filtre štatistík - počítané pri každom vykreslení, aby „dnes“
 *  po polnoci neostalo včerajšie. */
function quickRanges(): Array<[string, DateRange]> {
  const yesterday = toISODate(addDays(new Date(), -1))
  return [
    ['today', quickRange('today')],
    ['yesterday', { from: yesterday, to: yesterday }],
    ['thisWeek', quickRange('week')],
    ['lastWeek', shiftRange(quickRange('week'), -1)],
    ['thisMonth', quickRange('month')],
    ['lastMonth', shiftRange(quickRange('month'), -1)],
  ]
}
